import React from 'react';
import { Link } from 'react-router-dom';

export type ButtonVariant = 'fill' | 'outline' | 'link';

interface CommonProps {
  variant?: ButtonVariant;
  size?: 'default' | 'small';
  className?: string;
  children: React.ReactNode;
  'aria-label'?: string;
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
}

interface LinkProps extends CommonProps {
  /** In-app route. */
  to: string;
  href?: never;
  onClick?: never;
  type?: never;
  disabled?: never;
}

interface AnchorProps extends CommonProps {
  href: string;
  to?: never;
  onClick?: never;
  type?: never;
  disabled?: never;
  target?: string;
  rel?: string;
}

interface NativeProps extends CommonProps {
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  to?: never;
  href?: never;
}

export type ButtonProps = LinkProps | AnchorProps | NativeProps;

/**
 * The three button treatments from the mockups (brand/GUIDELINES.md §7):
 * Ink fill with white caps, a 1px outline in the current colour, and an
 * underlined text link. Square corners, Styrene A caps at 16.5% tracking.
 * Renders a router Link, an anchor, or a real <button> depending on props.
 */
export default function Button(props: ButtonProps) {
  const { variant = 'fill', size = 'default', className = '', children } = props;
  const classes = ['mk-btn', `mk-btn--${variant}`, size === 'small' ? 'mk-btn--small' : '', className]
    .filter(Boolean)
    .join(' ');
  const aria = {
    'aria-label': props['aria-label'],
    'aria-expanded': props['aria-expanded'],
    'aria-controls': props['aria-controls'],
    'aria-haspopup': props['aria-haspopup'],
  };

  if ('to' in props && props.to) {
    return (
      <Link to={props.to} className={classes} {...aria}>
        {children}
      </Link>
    );
  }
  if ('href' in props && props.href) {
    return (
      <a href={props.href} className={classes} target={props.target} rel={props.rel} {...aria}>
        {children}
      </a>
    );
  }
  const { onClick, type = 'button', disabled } = props as NativeProps;
  return (
    <button type={type} className={classes} onClick={onClick} disabled={disabled} {...aria}>
      {children}
    </button>
  );
}
